import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Autocomplete, Button, CircularProgress, IconButton, Step, StepLabel,
  Stepper, Switch, TextField,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { toast } from 'react-toastify';
import { useAuth } from '../../hook';
import { createSale, getAcquisitionChannels, getClients, getServices, previewSale } from './clientApi';
import { ClientConfirmDialog } from './ClientConfirmDialog';
import './client.scss';

const EXCHANGE_RATE_KEY = 'client_finance_exchange_rate';
const today = () => new Date().toISOString().slice(0, 10);
const money = (value, currency = 'PEN') => `${currency === 'USD' ? 'USD' : 'S/'} ${Number(value || 0).toFixed(2)}`;

function parsePercentages(value) {
  return value.split(',').map((item) => Number(item.trim())).filter((item) => Number.isFinite(item) && item > 0);
}

export function NewSale() {
  const navigate = useNavigate();
  const { auth } = useAuth();
  const [step, setStep] = useState(0);
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [channels, setChannels] = useState([]);
  const [client, setClient] = useState(null);
  const [clientText, setClientText] = useState('');
  const [selectedServices, setSelectedServices] = useState([]);
  const [primaryService, setPrimaryService] = useState(null);
  const [channel, setChannel] = useState(null);
  const [serviceText, setServiceText] = useState('');
  const [form, setForm] = useState({
    amount: '', invoiced: false, usd: false, detraction: false,
    exchangeRate: localStorage.getItem(EXCHANGE_RATE_KEY) || '',
    conditions: '50, 50', issueDate: today(), paymentDate: today(),
  });
  const [preview, setPreview] = useState(null);
  const [initialAmount, setInitialAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    Promise.all([getClients(auth.token), getServices(auth.token), getAcquisitionChannels(auth.token)])
      .then(([clientRows, serviceRows, channelRows]) => {
        setClients(clientRows); setServices(serviceRows); setChannels(channelRows);
      })
      .catch((err) => setError(err.message));
  }, [auth.token]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      getClients(auth.token, clientText.trim())
        .then(setClients)
        .catch((err) => setError(err.message));
    }, 350);
    return () => clearTimeout(timeout);
  }, [auth.token, clientText]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      getServices(auth.token, serviceText.trim())
        .then(setServices)
        .catch((err) => setError(err.message));
    }, 350);
    return () => clearTimeout(timeout);
  }, [auth.token, serviceText]);

  const percentages = useMemo(() => parsePercentages(form.conditions), [form.conditions]);
  const percentagesTotal = percentages.reduce((sum, value) => sum + value, 0);

  const previewPayload = (advance) => ({
    amount_without_igv: form.amount,
    currency: form.usd ? 'USD' : 'PEN',
    ...(form.usd ? { exchange_rate: form.exchangeRate } : {}),
    invoiced: form.invoiced,
    detraction_applied: form.detraction,
    detraction_rate: '12.00',
    initial_payment_amount: advance || '0.00',
    initial_payment_date: form.paymentDate,
  });

  const goToPreview = async () => {
    setError('');
    if (!client && !clientText.trim()) return setError('Selecciona o escribe un cliente.');
    if (!selectedServices.length) return setError('Selecciona al menos un servicio.');
    if (!channel?.id) return setError('Selecciona el canal de adquisición.');
    if (!(Number(form.amount) > 0)) return setError('Ingresa un monto válido.');
    if (Math.abs(percentagesTotal - 100) > 0.001) return setError('Las condiciones de pago deben sumar 100.');
    if (form.usd && !(Number(form.exchangeRate) > 0)) return setError('Ingresa un tipo de cambio válido.');

    setLoading(true);
    try {
      if (form.usd) localStorage.setItem(EXCHANGE_RATE_KEY, form.exchangeRate);
      const basePreview = await previewSale(auth.token, previewPayload('0.00'));
      const suggested = (Number(basePreview.sale.total) * percentages[0] / 100).toFixed(2);
      const completePreview = await previewSale(auth.token, previewPayload(suggested));
      setInitialAmount(suggested);
      setPreview(completePreview);
      setStep(1);
    } catch (err) {
      setError(err?.data ? JSON.stringify(err.data) : err.message);
    } finally {
      setLoading(false);
    }
  };

  const refreshPreview = async () => {
    setLoading(true); setError('');
    try { setPreview(await previewSale(auth.token, previewPayload(initialAmount))); }
    catch (err) { setError(err?.data ? JSON.stringify(err.data) : err.message); }
    finally { setLoading(false); }
  };

  const submit = async () => {
    setLoading(true); setError('');
    try {
      const existingClient = client && typeof client === 'object' && client.id;
      const payload = {
        ...(existingClient ? { client_id: client.id } : { client_create: { name: clientText.trim(), tax_id: '' } }),
        service_ids: selectedServices.map((service) => service.id),
        primary_service_id: primaryService?.id || selectedServices[0].id,
        acquisition_channel_id: channel.id,
        amount_without_igv: form.amount,
        currency: form.usd ? 'USD' : 'PEN',
        exchange_rate: form.usd ? form.exchangeRate : '1.0000',
        invoiced: form.invoiced,
        detraction_applied: form.detraction,
        detraction_rate: '12.00',
        issue_date: form.issueDate,
        payment_plan: percentages.map((percentage) => ({ percentage: String(percentage), due_date: null })),
        initial_payment: Number(initialAmount) > 0 ? {
          amount: initialAmount,
          date: form.paymentDate,
        } : null,
      };
      await createSale(auth.token, payload);
      toast.success('Venta y adelanto registrados correctamente.');
      setConfirmOpen(false);
      navigate('/', { replace: true });
    } catch (err) {
      setError(err?.data ? JSON.stringify(err.data) : err.message);
      toast.error('No se pudo registrar la venta. Revisa los datos.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="client-page client-sale-page">
      <header className="client-header">
        <IconButton onClick={() => step ? setStep(0) : navigate('/')}><ArrowBackIosNewIcon /></IconButton>
        <h1>Nueva venta</h1><span />
      </header>
      <Stepper activeStep={step} className="client-stepper"><Step><StepLabel>1</StepLabel></Step><Step><StepLabel>2</StepLabel></Step></Stepper>
      {error && <Alert severity="error">{error}</Alert>}

      {step === 0 ? (
        <div className="client-form">
          <Autocomplete freeSolo options={clients} value={client} inputValue={clientText}
            getOptionLabel={(option) => typeof option === 'string' ? option : option?.name || ''}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            onChange={(_, value) => setClient(value)} onInputChange={(_, value) => setClientText(value)}
            renderInput={(params) => <TextField {...params} label="Nombre del cliente" placeholder="Buscar o crear nuevo" />} />
          <Autocomplete multiple options={services} value={selectedServices}
            inputValue={serviceText} getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(_, value) => {
              setSelectedServices(value);
              if (!value.some((service) => service.id === primaryService?.id)) {
                setPrimaryService(value[0] || null);
              }
            }}
            onInputChange={(_, value) => setServiceText(value)}
            renderInput={(params) => <TextField {...params} label="Servicios contratados" />} />
          {selectedServices.length > 0 && <Autocomplete options={selectedServices}
            value={primaryService || selectedServices[0]}
            getOptionLabel={(option) => option?.name || ''}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            onChange={(_, value) => setPrimaryService(value)}
            renderInput={(params) => <TextField {...params} label="Servicio principal" helperText="Se usará en el Resumen General" />} />}
          <Autocomplete options={channels} value={channel}
            getOptionLabel={(option) => option?.name || ''}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            onChange={(_, value) => setChannel(value)}
            renderInput={(params) => <TextField {...params} label="Canal de adquisición" placeholder="Seleccionar canal" />} />
          <TextField label="Fecha de venta" type="date" value={form.issueDate}
            onChange={(e) => setForm({ ...form, issueDate: e.target.value })} InputLabelProps={{ shrink: true }} />
          <TextField label="Total sin IGV" type="number" value={form.amount}
            onChange={(e) => setForm({ ...form, amount: e.target.value })} />
          <div className="client-row">
            <label className="client-switch">Aplicar IGV (18%)<Switch checked={form.invoiced}
              onChange={(e) => setForm({ ...form, invoiced: e.target.checked, detraction: e.target.checked ? form.detraction : false })} /></label>
            <label className="client-switch">Dólares<Switch checked={form.usd}
              onChange={(e) => setForm({ ...form, usd: e.target.checked })} /></label>
          </div>
          <div className="client-row">
            <label className="client-switch">Detracción (12%)<Switch checked={form.detraction} disabled={!form.invoiced}
              onChange={(e) => setForm({ ...form, detraction: e.target.checked })} /></label>
            {form.usd && <TextField label="Tipo de cambio" type="number" value={form.exchangeRate}
              onChange={(e) => setForm({ ...form, exchangeRate: e.target.value })} />}
          </div>
          <TextField label="Condiciones de pago" value={form.conditions}
            onChange={(e) => setForm({ ...form, conditions: e.target.value })}
            helperText={`Separadas por coma. Suma actual: ${percentagesTotal}%`} />
          <Button variant="contained" className="client-primary" onClick={goToPreview} disabled={loading}>
            {loading ? <CircularProgress size={22} color="inherit" /> : 'Siguiente'}
          </Button>
        </div>
      ) : preview && (
        <div className="client-form">
          <TextField label="Fecha de pago" type="date" value={form.paymentDate}
            onChange={(e) => setForm({ ...form, paymentDate: e.target.value })} InputLabelProps={{ shrink: true }} />
          <div className="client-summary">
            <Value label="Monto ingresado" value={money(form.amount, form.usd ? 'USD' : 'PEN')} />
            <Value label="Conversión" value={money(preview.sale.amount_without_igv_pen)} />
            <Value label="IGV" value={money(preview.sale.igv, form.usd ? 'USD' : 'PEN')} />
            <Value label="Total" value={money(preview.sale.total, form.usd ? 'USD' : 'PEN')} />
          </div>
          <h2>Datos de adelanto</h2>
          <TextField label="Monto total cancelado (incluye detracción)" type="number" value={initialAmount}
            onChange={(e) => setInitialAmount(e.target.value)} onBlur={refreshPreview} />
          <div className="client-summary">
            <Value label="Base" value={money(preview.initial_payment.base, form.usd ? 'USD' : 'PEN')} />
            <Value label="IGV" value={money(preview.initial_payment.igv, form.usd ? 'USD' : 'PEN')} />
            <Value label="Detracción" value={form.usd
              ? `${money(preview.initial_payment.detraction_amount, 'USD')} · ${money(Number(preview.initial_payment.detraction_amount) * Number(form.exchangeRate))}`
              : money(preview.initial_payment.detraction_amount, 'PEN')} />
            <Value label="Depósito a la agencia" value={money(preview.initial_payment.net_bank_amount, form.usd ? 'USD' : 'PEN')} />
          </div>
          <Button variant="contained" className="client-primary" onClick={() => setConfirmOpen(true)} disabled={loading}>
            {loading ? <CircularProgress size={22} color="inherit" /> : 'Subir venta'}
          </Button>
        </div>
      )}
      <ClientConfirmDialog
        open={confirmOpen}
        title="¿Registrar esta venta?"
        message={preview
          ? `Se aplicarán ${money(initialAmount, form.usd ? 'USD' : 'PEN')} a la deuda: ${money(preview.initial_payment.net_bank_amount, form.usd ? 'USD' : 'PEN')} depositados a la agencia y ${money(preview.initial_payment.detraction_amount, form.usd ? 'USD' : 'PEN')} destinados a detracciones${form.usd ? ` (${money(Number(preview.initial_payment.detraction_amount) * Number(form.exchangeRate))})` : ''}.`
          : 'Confirma los datos de la venta.'}
        confirmText="Sí, registrar"
        loading={loading}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={submit}
      />
    </main>
  );
}

function Value({ label, value }) {
  return <div className="client-value"><span>{label}</span><strong>{value}</strong></div>;
}
